// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import ProductListContainer from "@pacific/App/Components/Product/ProductListTemplate";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@pacific/App/Components/Product/ProductPriceAndActions";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@pacific/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@pacific/Overrides/Widgets/ProductList/ProductListProductInformation";
import { mergeWithCss } from "@pacific/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductCardProps {
    extendedStyles?: ProductListProductCardStyles;
    showOverlayBadges?: boolean;
}
type Props = ProductCardSelections & HasProduct & ProductCardProps;
export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productListProductImageStyles?: ProductListProductImageStyles;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
    productListProductInformationStyles?: ProductListProductInformationStyles;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    productListProductImageStyles: {
        compareCheckbox: {
            css: css`
                margin-top: 42px;
            `,
        },
        productImage: {
            linkWrappingImage: {
                css: css`
                    position: relative;
                    height: 100px;
                    width: 100px;
                    padding: 0;
                    margin: 0 auto;
                `,
            },
            img: {
                width: 100,
                height: 100,
                css: css`
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                `,
                urlOptions: "t_prdsm",
            } as any,
        },
    },
    productPriceAndActionsStyles: {
        productGridContainer: {
            container: {
                css: css`
                    max-width: 285px;
                `,
            },
            priceAndActions: {
                css: css`
                    height: 100%;
                `,
            },
        },
    },
};

// XNG-Changes: change structure
const ProductListProductCard: FC<Props> = props => {
    const [styles] = useState(() => mergeWithCss(productCardStyles, props.extendedStyles));

    return (
        <ProductListContainer data-test-selector={`productListProductCard${props.product.id}`}>
            <ProductListContainer.Image>
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={props.showCompare}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    extendedStyles={styles.productListProductImageStyles}
                    displayOverlayBadges={props.showOverlayBadges}
                />
            </ProductListContainer.Image>
            <ProductListContainer.Info>
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={false}
                    showAvailability={false}
                    showAttributes={false}
                    showImageBadges={false}
                    showTextBadges={false}
                    showTagsAndBadges={false}
                    extendedStyles={styles.productListProductInformationStyles}
                />
                <ProductListContainer.ContentWrapper>
                    <ProductListProductInformation
                        showBrand={false}
                        showTitle={false}
                        showPartNumbers={props.showPartNumbers}
                        showAvailability={false}
                        showAttributes={false}
                        showImageBadges={false}
                        showTextBadges={false}
                        showTagsAndBadges={false}
                    />
                    <ProductListProductInformation
                        showBrand={false}
                        showTitle={false}
                        showPartNumbers={false}
                        showAvailability={false}
                        showAttributes={false}
                        showImageBadges={props.showImageBadges}
                        maxImageBadges={props.maximumNumberOfImageBadges}
                        showTextBadges={props.showTextBadges}
                        maxTextBadges={props.maximumNumberOfTextBadges}
                        showTagsAndBadges={false}
                    />
                </ProductListContainer.ContentWrapper>
                <ProductSearchScoreData />
            </ProductListContainer.Info>
            <ProductPriceAndActions
                showAvailability={props.showAvailability}
                showAddToList={props.showAddToList}
                showPrice={props.showPrice}
                extendedStyles={styles.productPriceAndActionsStyles}
            />
        </ProductListContainer>
    );
};

export default withProduct(ProductListProductCard);
